import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { resolveDir } from '../components/base/primitiveUtils';

/**
 * Keyboard movement through a list of options: the "active" (highlighted) index that
 * `aria-activedescendant` points at, or that a roving tabindex follows.
 *
 * - Vertical lists: ArrowUp / ArrowDown. Horizontal: ArrowLeft / ArrowRight, MIRRORED
 *   in RTL so the arrow always moves the way the eye reads. Grids do both.
 * - Home / End, PageUp / PageDown, Enter (and optionally Space) to select.
 * - Typeahead that understands Arabic spelling: typing «ر» finds «الرياض», and
 *   «جده» finds «جدة» (alef forms, taa marbuta, alef maqsura and harakat are folded).
 * - Disabled options are skipped.
 */

export type ListOrientation = 'vertical' | 'horizontal' | 'grid';

export interface UseListNavigationOptions {
  count: number;
  /** Controlled active index (-1 = none). */
  activeIndex?: number;
  defaultActiveIndex?: number;
  onActiveIndexChange?: (index: number) => void;
  orientation?: ListOrientation;
  /** Columns, for `orientation: 'grid'`. */
  columns?: number;
  /** Wrap from the last option to the first and back. Default false. */
  loop?: boolean;
  /** PageUp / PageDown jump. Default 10. */
  pageSize?: number;
  /** Handle Home / End. Turn off inside an editable combobox, where they move the caret. */
  homeEnd?: boolean;
  isDisabled?: (index: number) => boolean;
  /** Option text; enables typeahead when given. */
  getLabel?: (index: number) => string;
  /** Called on Enter (and Space when `selectOnSpace`). */
  onSelect?: (index: number) => void;
  selectOnSpace?: boolean;
}

export interface ListNavigation {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  /** Handles one key. Returns true (after preventDefault) when it used the key. */
  handleKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => boolean;
  /** Feed one printable character to typeahead; returns the matched index or -1. */
  typeahead: (char: string) => number;
  firstEnabled: () => number;
  lastEnabled: () => number;
}

const HARAKAT = /[ؐ-ًؚ-ٰٟۖ-ۭ]/g;
const TATWEEL = /ـ/g;

/**
 * Fold the spelling variants people actually type, so matching is forgiving:
 * أ إ آ ٱ → ا, ة → ه, ى → ي, ؤ → و, ئ → ي; harakat and tatweel dropped;
 * Latin lower-cased; whitespace collapsed.
 */
export function normalizeForMatch(text: string): string {
  return text
    .replace(HARAKAT, '')
    .replace(TATWEEL, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Does `label` start with `query`, ignoring a leading «ال»? Both pre-normalised. */
function startsWithLoose(label: string, query: string): boolean {
  if (label.startsWith(query)) return true;
  return label.startsWith('ال') && label.slice(2).startsWith(query);
}

const TYPEAHEAD_RESET_MS = 600;

export function useListNavigation({
  count,
  activeIndex: controlled,
  defaultActiveIndex = -1,
  onActiveIndexChange,
  orientation = 'vertical',
  columns = 1,
  loop = false,
  pageSize = 10,
  homeEnd = true,
  isDisabled,
  getLabel,
  onSelect,
  selectOnSpace = false,
}: UseListNavigationOptions): ListNavigation {
  const [inner, setInner] = useState(defaultActiveIndex);
  const activeIndex = controlled ?? inner;

  const setActiveIndex = useCallback(
    (index: number) => {
      if (controlled === undefined) setInner(index);
      onActiveIndexChange?.(index);
    },
    [controlled, onActiveIndexChange],
  );

  const enabled = useCallback((i: number) => i >= 0 && i < count && !isDisabled?.(i), [count, isDisabled]);

  const firstEnabled = useCallback(() => {
    for (let i = 0; i < count; i += 1) if (enabled(i)) return i;
    return -1;
  }, [count, enabled]);

  const lastEnabled = useCallback(() => {
    for (let i = count - 1; i >= 0; i -= 1) if (enabled(i)) return i;
    return -1;
  }, [count, enabled]);

  /** One step forward/back, skipping disabled options; wraps when `loop`. */
  const step = useCallback(
    (from: number, delta: 1 | -1) => {
      if (count <= 0) return -1;
      if (from < 0) return delta > 0 ? firstEnabled() : lastEnabled();
      let i = from;
      for (let n = 0; n < count; n += 1) {
        i += delta;
        if (i < 0 || i >= count) {
          if (!loop) return from;
          i = (i + count) % count;
        }
        if (enabled(i)) return i;
      }
      return from;
    },
    [count, enabled, firstEnabled, lastEnabled, loop],
  );

  /** PageUp / PageDown: several options at once, clamped to the ends, off disabled ones. */
  const jump = useCallback(
    (from: number, delta: number) => {
      if (count <= 0) return -1;
      if (from < 0) return delta > 0 ? firstEnabled() : lastEnabled();
      const target = Math.min(count - 1, Math.max(0, from + delta));
      const dir = delta > 0 ? 1 : -1;
      for (let i = target; i >= 0 && i < count; i += dir) if (enabled(i)) return i;
      for (let i = target - dir; i !== from && i >= 0 && i < count; i -= dir) if (enabled(i)) return i;
      return from;
    },
    [count, enabled, firstEnabled, lastEnabled],
  );

  /** Grid row move: straight up or down one row; stays put at the edge. */
  const rowMove = useCallback(
    (from: number, rows: 1 | -1) => {
      if (count <= 0) return -1;
      if (from < 0) return rows > 0 ? firstEnabled() : lastEnabled();
      const target = from + rows * Math.max(1, columns);
      if (target < 0 || target >= count || !enabled(target)) return from;
      return target;
    },
    [columns, count, enabled, firstEnabled, lastEnabled],
  );

  // ---- typeahead
  const bufferRef = useRef('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const typeahead = useCallback(
    (char: string) => {
      if (!getLabel || count <= 0) return -1;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        bufferRef.current = '';
      }, TYPEAHEAD_RESET_MS);
      bufferRef.current += char;
      const raw = bufferRef.current;
      // Typing the same letter repeatedly cycles through options starting with it.
      const repeated = raw.length > 1 && raw.split('').every((c) => c === raw[0]);
      const query = normalizeForMatch(repeated ? raw[0] : raw);
      if (!query) return -1;
      const startAt = repeated || raw.length === 1 ? activeIndex + 1 : Math.max(activeIndex, 0);
      for (let n = 0; n < count; n += 1) {
        const i = (startAt + n + count) % count;
        if (!enabled(i)) continue;
        if (startsWithLoose(normalizeForMatch(getLabel(i)), query)) {
          setActiveIndex(i);
          return i;
        }
      }
      return -1;
    },
    [activeIndex, count, enabled, getLabel, setActiveIndex],
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (event.nativeEvent.isComposing) return false;
      const rtl = resolveDir(event.currentTarget) === 'rtl';
      const forwardKey = rtl ? 'ArrowLeft' : 'ArrowRight';
      const backKey = rtl ? 'ArrowRight' : 'ArrowLeft';
      let next: number | null = null;
      const key = event.key;

      if (orientation !== 'horizontal' && key === 'ArrowDown') {
        next = orientation === 'grid' ? rowMove(activeIndex, 1) : step(activeIndex, 1);
      } else if (orientation !== 'horizontal' && key === 'ArrowUp') {
        next = orientation === 'grid' ? rowMove(activeIndex, -1) : step(activeIndex, -1);
      } else if (orientation !== 'vertical' && key === forwardKey) {
        next = step(activeIndex, 1);
      } else if (orientation !== 'vertical' && key === backKey) {
        next = step(activeIndex, -1);
      } else if (homeEnd && key === 'Home') {
        next = firstEnabled();
      } else if (homeEnd && key === 'End') {
        next = lastEnabled();
      } else if (key === 'PageDown') {
        next = jump(activeIndex, pageSize);
      } else if (key === 'PageUp') {
        next = jump(activeIndex, -pageSize);
      } else if (key === 'Enter' || (key === ' ' && selectOnSpace && !bufferRef.current)) {
        if (activeIndex >= 0 && enabled(activeIndex) && onSelect) {
          event.preventDefault();
          onSelect(activeIndex);
          return true;
        }
        return false;
      } else if (
        getLabel &&
        key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        (key !== ' ' || bufferRef.current)
      ) {
        event.preventDefault();
        typeahead(key);
        return true;
      }

      if (next === null) return false;
      event.preventDefault();
      if (next !== activeIndex && next >= 0) setActiveIndex(next);
      return true;
    },
    [
      activeIndex,
      enabled,
      firstEnabled,
      getLabel,
      homeEnd,
      jump,
      lastEnabled,
      onSelect,
      orientation,
      pageSize,
      rowMove,
      selectOnSpace,
      setActiveIndex,
      step,
      typeahead,
    ],
  );

  return { activeIndex, setActiveIndex, handleKeyDown, typeahead, firstEnabled, lastEnabled };
}

export default useListNavigation;
